import React, { Children } from "react";

import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { themes } from "prism-react-renderer";
import { Highlight } from "prism-react-renderer";
// import { dracula } from 'react-syntax-highlighter/dist/esm/styles/prism';

import "components/challenges/multiChoice/MultiChoiceOption.css"

const theme = themes.dracula;

const customStyle = {
    width: "fit-content",
    borderRadius: '0',
    backgroundColor: 'transparent',
    padding: '0',
    margin: '0',
    fontSize: '18px',
    fontWeight: 'bold',
};

// Utility: All components accept fontSize (Tailwind class, e.g. 'text-xl')
const baseFont = "";
const baseText = "text-xl";
const baseColor = "text-text";
const baseSpacing = "my-2";

export const CustomLink = ({ href, children }) => (
    <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={`underline text-primary font-bold hover:text-accent transition-colors duration-200 ${baseFont} ${baseText}`}
    >
        {children}
    </a>
);

export const CustomQuote = ({ children }) => (
    <div className={`bg-background-light mt-5 rounded-xl overflow-hidden shadow-md ${baseSpacing}`}>
        <blockquote className={`flex gap-2 items-start border-l-[4px] border-accent px-4 py-2 bg-background-light font-bold *:text-lg ${baseColor} `}>
            <span className="text-accent my-2">Tip: </span>{children}
        </blockquote>
    </div>
);

function flattenChildren(children) {
    let result = '';

    Children.forEach(children, (child) => {
        if (typeof child === 'string') {
            result += child;
        } else if (child?.type === 'br') {
            result += '\n';
        } else if (typeof child === 'object' && child?.props?.children) {
            result += flattenChildren(child.props.children);
        }
    });

    return result;
}

export const CustomInlineCode = ({ children }) => {

    let raw = flattenChildren(children);

    // Normalize <br/> to \n and trim lines
    raw = raw.replace(/<br\s*\/?>/gi, '\n');
    let isMultiLine = raw.includes('\n');
    let cleaned = isMultiLine
        ? raw.split('\n').map(line => line).filter(Boolean).join('\n')
        : raw;
    // console.log("Raw:", cleaned);

    return <Highlight code={cleaned} language="python" theme={theme}>
        {({ tokens, getLineProps, getTokenProps }) => (
            <code className="bg-code min-w-max inline-block text-primary px-2 py-[0.1rem] whitespace-pre rounded font-mono text-base font-semibold text-white">
                {tokens.map((line, i) => {
                    const { key, ...lineProps } = getLineProps({ line });
                    return (<div key={i} {...lineProps}>
                        {line.map((token, key) => {
                            const { key: tokenKey, ...tokenProps } = getTokenProps({ token });
                            return <span key={key} {...tokenProps} />
                        })}
                    </div>
                    )
                })}
            </code>
        )}
    </Highlight>
};

export const CustomItalic = ({ children }) => (
    <span className="underline underline-offset-2 decoration-2 decoration-white/50">{children}</span>
);

export const CustomHeading1 = ({ children }) => (
    <h1 className="text-3xl font-bold text-primary my-4 leading-tight drop-shadow-lg">{children}</h1>
);
export const CustomHeading2 = ({ children }) => (
    <h2 className="text-3xl font-bold text-primary my-3 leading-tight">{children}</h2>
);
export const CustomHeading3 = ({ children }) => (
    <h3 className="text-3xl font-bold text-text my-2 leading-relaxed">{children}</h3>
);
export const CustomHeading4 = ({ children }) => (
    <h4 className="text-xl font-bold text-primary my-2 leading-tight">{children}</h4>
);
export const CustomHeading5 = ({ children }) => (
    <h5 className="text-lg font-bold text-primary my-1 leading-tight">{children}</h5>
);
export const CustomHeading6 = ({ children }) => (
    <h6 className="text-base font-bold text-primary my-1 leading-tight">{children}</h6>
);

export const CustomHr = () => (
    <hr className="border-t-2 border-border my-6" />
);

export const CustomImg = ({ src, alt }) => (
    <img src={src} alt={alt} className="rounded-xl shadow-lg max-w-full my-4 border-2 border-border" />
);

export const CustomListItem = ({ children }) => (
    <li className="ml-6 list-disc text-text text-xl my-1">{children}</li>
);
export const CustomOrderedList = ({ children }) => (
    <ol className="ml-6 list-decimal text-text text-xl my-2">{children}</ol>
);
export const CustomUnorderedList = ({ children }) => (
    <ul className="ml-6 list-disc text-text text-xl my-2">{children}</ul>
);

export const CustomParagraph = ({ children }) => (
    <p className={`leading-7 ${baseFont} ${baseText} ${baseColor} ${baseSpacing}`}>{children}</p>
);

export const CustomPreBlock = ({ children }) => {

    let raw = flattenChildren(children);

    // Normalize <br/> to \n and trim lines
    raw = raw.replace(/<br\s*\/?>/gi, '\n');
    let isMultiLine = raw.includes('\n');
    let cleaned = isMultiLine
        ? raw.split('\n').map(line => line).filter(Boolean).join('\n')
        : raw.trim();

    return (
        <pre className="ml-0 bg-code-light text-accent font-mono rounded-xl p-3 w-fit min-w-96 overflow-x-auto my-4 text-base border-2 border-border shadow-md">
            <div className="bg-code p-3 py-4 pr-6 rounded-[.5rem] w-full flex items-center text-white">
                <SyntaxHighlighter
                    language="python"
                    style={theme}
                    customStyle={customStyle}
                    codeTagProps={{}}
                    showLineNumbers={true}
                    lineNumberStyle={{ color: "#ddd" }}
                    wrapLongLines
                >
                    {cleaned}
                </SyntaxHighlighter>
            </div>
        </pre>)
};

export const CustomBold = ({ children }) => (
    <strong className="font-bold text-primary">{children}</strong>
);

export const CustomTable = ({ children }) => (
    <table className="w-full border-collapse my-4 rounded-xl overflow-hidden bg-background-light border-2 box-border border-border text-text text-xl">
        {children}
    </table>
);
export const CustomTableHead = ({ children }) => (
    <thead className="bg-background border-b-2 border-border *:divide-x-2 *:divide-border-light *:divide-y-0">{children}</thead>
);
export const CustomTableBody = ({ children }) => (
    <tbody>{children}</tbody>
);
export const CustomTableRow = ({ children }) => (
    <tr className="even:bg-background ">{children}</tr>
);
export const CustomTableCell = ({ children }) => (
    <td className="px-4 py-2 border-b border-r border-border text-text text-lg">{children}</td>
);
export const CustomTableHeader = ({ children }) => (
    <th className="px-4 py-2 border-b-2 border-border text-text text-xl font-bold bg-border">

        {children}
    </th>
);




// Export as a single object for easy import
const MarkdownComponents = {
    a: CustomLink,
    blockquote: CustomQuote,
    code: CustomInlineCode,
    em: CustomItalic,
    h1: CustomHeading1,
    h2: CustomHeading2,
    h3: CustomHeading3,
    h4: CustomHeading4,
    h5: CustomHeading5,
    h6: CustomHeading6,
    hr: CustomHr,
    img: CustomImg,
    li: CustomListItem,
    ol: CustomOrderedList,
    p: CustomParagraph,
    pre: CustomPreBlock,
    strong: CustomBold,
    ul: CustomUnorderedList,
    table: CustomTable,
    thead: CustomTableHead,
    tbody: CustomTableBody,
    tr: CustomTableRow,
    td: CustomTableCell,
    th: CustomTableHeader,
};



export default MarkdownComponents;
